import { Box, IconButton, Paper, Tab, Tabs, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import React, { useEffect, useState, useRef } from 'react';
import SwipeableViews from 'react-swipeable-views';
import api from '../../../../services/api/base';
import { FeaturedCardType, ICollection, IFeaturedCard, ILink } from '../../../../services/types';
import { CollectionList } from '../../../../components/CollectionList';
import Detail from './Detail';
import LinkList from './LinkList';
import StoryList from '../../../../components/StoryList';
import useStyles from './styles';
import { getMatchedSeason } from '../../../../services/utils';
import { Canceler } from 'axios';
import useSnackbar from '../../../../hooks/useSnackbar';
import { getCollections, searchCollectionByTitle } from '../../../../services/api/Collection';
import { getLinks } from '../../../../services/api/Link';
import { getFeaturedCards } from '../../../../services/api/FeaturedCard';
import FeaturedCard from './FeaturedCard';

interface IProps {
  onClose: () => void;
  onSave: (f?: IFeaturedCard, createAnother?: boolean) => void;
  featuredCard?: IFeaturedCard;
  mode: 'edit' | 'create';
  featuredCardId?: number;
  slotNo?: number;
}

const AddModal = (props: IProps): JSX.Element => {
  const { mode } = props;

  // uses
  const classes = useStyles();
  const snackbar = useSnackbar();

  // states
  const [createdCount, setCreatedCount] = useState<number>(0);
  const [phaseIndex, setPhaseIndex] = useState<number>(0);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [currentFeaturedCard, setCurrentFeaturedCard] = useState<Partial<IFeaturedCard>>();
  const [links, setLinks] = useState<ILink[]>();
  const [linksTotalCount, setLinksTotalCount] = useState<number>(0);
  const [collections, setCollections] = useState<ICollection[]>();
  const [collectionsTotalCount, setCollectionsTotalCount] = useState<number | undefined>(0);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // refs
  const linksCanceler = useRef<Canceler>(() => undefined);
  const collectionsCanceler = useRef<Canceler>(() => undefined);
  const searchCollectionsCanceler = useRef<Canceler>(() => undefined);

  // handlers
  const onPressSave = async (f: IFeaturedCard, createAnother: boolean) => {
    if (createAnother) {
      setCreatedCount(createdCount + 1);
      setPhaseIndex(0);
    } else {
      props.onSave(f);
    }
  };
  const onPressCancel = () => {
    setPhaseIndex(0);
    if (mode === 'edit') {
      if (createdCount > 0) {
        props.onSave();
      }
      props.onClose();
    }
  };
  const onPressClose = () => {
    if (createdCount > 0) {
      props.onSave();
    }
    props.onClose();
  };

  // API
  const fetchCollections = async (page: number, rowsPerPage: number) => {
    collectionsCanceler.current();

    try {
      const [fetchPromise, cancel] = getCollections(page + 1, rowsPerPage);
      collectionsCanceler.current = cancel;
      const result = await fetchPromise;

      if (result.data?.collections) {
        setCollections(result.data.collections);
        setCollectionsTotalCount(result.data.totalCount);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };
  const searchCollections = async (title: string) => {
    searchCollectionsCanceler.current();

    try {
      const [fetchPromise, cancel] = searchCollectionByTitle(title);
      searchCollectionsCanceler.current = cancel;
      const result = await fetchPromise;

      if (result.data?.items) {
        setCollections(result.data.items.map(item => item.collection));
        setCollectionsTotalCount(undefined);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };
  const fetchLinks = async (page: number, rowsPerPage: number) => {
    linksCanceler.current();

    try {
      const [fetchPromise, cancel] = getLinks(page + 1, rowsPerPage);
      linksCanceler.current = cancel;

      const result = await fetchPromise;

      if (result.data?.links) {
        setLinks(result.data.links);
        setLinksTotalCount(result.data.totalCount);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };
  const fetchFeaturedCard = async (id: number) => {
    try {
      const [fetchPromise, canceler] = getFeaturedCards(undefined, id.toString(), 0, 1);
      const result = await fetchPromise;
      if (result.data?.items && result.data?.items.length > 0) {
        setCurrentFeaturedCard(result.data?.items[0]);
      } else {
        throw 'Could not found.';
      }
    } catch (e) {
      console.log(e);
      snackbar.error(e);
    } finally {
    }
  };

  // watches
  useEffect(() => {
    if (mode === 'edit') {
      setPhaseIndex(1);
      setCurrentFeaturedCard(props.featuredCard);

      switch (props.featuredCard?.type) {
        case 'story':
          setTabIndex(0);
          break;
        case 'link':
          setTabIndex(1);
          break;
        case 'collection':
          setTabIndex(2);
          break;
      }
    }
  }, [mode, props.featuredCard]);
  useEffect(() => {
    if (tabIndex === 1 && links === undefined) {
      fetchLinks(0, 20);
    }

    if (tabIndex === 0 && collections === undefined) {
      fetchCollections(0, 20);
    }
  }, [collections, links, tabIndex]);
  useEffect(() => {
    return () => {
      linksCanceler.current();
      collectionsCanceler.current();
      searchCollectionsCanceler.current();
    };
  });

  // init
  useEffect(() => {
    if (props.featuredCardId) {
      fetchFeaturedCard(props.featuredCardId);
    }
  }, []);

  return (
    <Box className={classes.addModalRoot}>
      <Paper className={classes.modalPaper}>
        <Box className={classes.titleWrap}>
          <Typography component="h6" variant="subtitle1" style={{ fontWeight: 500 }}>
            {mode === 'edit' ? props.featuredCard?.title : 'Create a Featured Card'}
          </Typography>
        </Box>
        <IconButton className={classes.modalCloseButton} onClick={onPressClose}>
          <CloseIcon style={{ color: '#bebebe' }} />
        </IconButton>
        {mode === 'create' && (
          <Tabs
            value={tabIndex}
            onChange={(e, value) => {
              setTabIndex(value);
              setPhaseIndex(0);
            }}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              label="Collection"
              classes={{
                root: classes.addModalTabRoot,
                wrapper: classes.addModalTabWrapper,
              }}
            />
            <Tab
              label="Link"
              classes={{
                root: classes.addModalTabRoot,
                wrapper: classes.addModalTabWrapper,
              }}
            />
            <Tab
              label="Story"
              classes={{
                root: classes.addModalTabRootLeft,
                wrapper: classes.addModalTabWrapper,
              }}
            />
          </Tabs>
        )}
        {mode === 'edit' && (
          <Tabs value={0} onChange={() => {}} indicatorColor="primary" textColor="primary">
            <Tab
              label={props.featuredCard?.type}
              classes={{
                root: classes.addModalTabRoot,
                wrapper: classes.addModalTabWrapper,
              }}
            />
          </Tabs>
        )}
        <Box display="flex">
          <Box
            style={{
              width: tabIndex === 0 ? '100%' : '0',
            }}
            visibility={tabIndex === 0 ? 'visible' : 'hidden'}
            className={classes.addModalTabContentWrap}
          >
            <SwipeableViews index={phaseIndex} animateHeight={true} animateTransitions={mode === 'create'} disableLazyLoading={false}>
              <CollectionList
                height={366}
                compact={true}
                collections={collections}
                totalCount={collectionsTotalCount}
                onRequestFetch={(page, rowsPerPage) => fetchCollections(page, rowsPerPage)}
                onRequestSearch={keyword => searchCollections(keyword)}
                onSelectCollection={collection => {
                  setPhaseIndex(1);
                  setCurrentFeaturedCard({
                    targetId: collection.trackingId!,
                    title: collection.title,
                    coverUrl: collection.coverUrl || '',
                    storyTypes: collection.storyTypes,
                    type: FeaturedCardType.Collection,
                    availableCountryCountText: collection.availableCountryCountText,
                  });
                }}
              />
              <Detail
                mode={props.mode}
                featuredCard={currentFeaturedCard}
                object={currentFeaturedCard?.object}
                onPressCancel={onPressCancel}
                onPressSave={onPressSave}
              />
            </SwipeableViews>
          </Box>
          <Box
            style={{
              width: tabIndex === 1 ? '100%' : '0',
            }}
            visibility={tabIndex === 1 ? 'visible' : 'hidden'}
            className={classes.addModalTabContentWrap}
          >
            <SwipeableViews index={phaseIndex} animateHeight={true} animateTransitions={mode === 'create'} disableLazyLoading={true}>
              <LinkList
                links={links}
                totalCount={linksTotalCount}
                onRequestFetch={(page, rowsPerPage) => fetchLinks(page, rowsPerPage)}
                onSelectLink={link => {
                  setPhaseIndex(1);
                  setCurrentFeaturedCard({
                    targetId: link.id?.toString(),
                    title: link.title,
                    coverUrl: link.coverUrl || '',
                    type: FeaturedCardType.Link,
                  });
                }}
              />
              <Detail
                mode={props.mode}
                featuredCard={currentFeaturedCard}
                object={currentFeaturedCard?.object}
                onPressCancel={onPressCancel}
                onPressSave={onPressSave}
              />
            </SwipeableViews>
          </Box>
          <Box
            style={{
              width: tabIndex === 2 ? '100%' : '0',
            }}
            visibility={tabIndex === 2 ? 'visible' : 'hidden'}
            className={classes.addModalTabContentWrap}
          >
            <SwipeableViews index={phaseIndex} animateHeight={true} animateTransitions={mode === 'create'} disableLazyLoading={true}>
              <StoryList
                onItemClick={story => {
                  setPhaseIndex(1);
                  const season = getMatchedSeason(story);
                  setCurrentFeaturedCard({
                    targetId: story.seasonId ? story.seasonId.toString() : story.id.toString(),
                    type: story.seasonId ? FeaturedCardType.Season : FeaturedCardType.Story,
                    title: story.seasonId && season!.title ? season!.title : story.title,
                    coverUrl: '',
                    object: { story },
                    availableCountryCountText: story.availableCountryCountText,
                  });
                }}
                readonly={true}
                height={366}
                fullWidth={true}
              />
              <Detail
                mode={props.mode}
                featuredCard={currentFeaturedCard}
                object={currentFeaturedCard?.object}
                onPressCancel={onPressCancel}
                onPressSave={onPressSave}
              />
            </SwipeableViews>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddModal;
